import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, Edit2, Trash2, Plus, User, Mail, Phone, Globe, MapPin, MoreVertical, CheckCircle, DollarSign, FolderOpen, Clock, TrendingUp, Calendar, Crown, CreditCard } from 'lucide-react';
import { useAuth, useModal } from '../../../hooks';
import { Client, Project, ClientContact, NewClientContact } from '../../../types';
import api from '../../../services/api';
import { ContactForm } from '../ContactForm/ContactForm';
import { Button, Badge, Card, EmptyState, LoadingSpinner } from '../../common';

// Client Detail Component - Modern Redesign
const ClientDetail: React.FC<{ client: Client; onBack: () => void; onSelectProject?: (project: Project) => void }> = ({ client, onBack, onSelectProject }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'contacts' | 'projects' | 'notes'>('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Client>(client);
  const [saving, setSaving] = useState(false);
  const { isOpen: showDeleteModal, openModal: openDeleteModal, closeModal: closeDeleteModal } = useModal();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [contacts, setContacts] = useState<ClientContact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactsUpdateKey, setContactsUpdateKey] = useState(0);
  const contactsRef = useRef<ClientContact[]>([]);
  const { token, isAuthenticated, loading } = useAuth();
  const { isOpen: showAddContactModal, openModal: openAddContactModal, closeModal: closeAddContactModal } = useModal();
  const [addingContact, setAddingContact] = useState(false);
  const { isOpen: isEditContactModalOpen, openModal: openEditContactModal, closeModal: closeEditContactModal } = useModal();
  const [editingContact, setEditingContact] = useState<ClientContact | null>(null);

  const fetchClientProjects = useCallback(async () => {
    if (loading || !isAuthenticated || !token) {
      setLoadingProjects(false);
      return;
    }

    try {
      setLoadingProjects(true);
      const response = await api.get<any>(`/api/clients/${client.id}/projects`, token);
      setProjects(response.projects || []);
    } catch (error) {
      console.error('Failed to fetch client projects:', error);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, [client.id, token, isAuthenticated, loading]);

  const fetchClientContacts = useCallback(async () => {
    if (loading || !isAuthenticated || !token) {
      setLoadingContacts(false);
      return;
    }

    try {
      setLoadingContacts(true);
      const response = await api.get<any>(`/api/clients/${client.id}/contacts`, token);

      const transformedContacts = (response.contacts || [])
        .map((contact: any) => ({
          id: contact.id,
          clientId: contact.clientId || contact.client_id,
          firstName: contact.firstName || contact.first_name,
          lastName: contact.lastName || contact.last_name,
          email: contact.email,
          phone: contact.phone,
          position: contact.position,
          department: contact.department,
          isPrimary: contact.isPrimary || contact.is_primary,
          isBilling: contact.isBilling || contact.is_billing,
          notes: contact.notes,
          createdAt: contact.createdAt || contact.created_at,
          updatedAt: contact.updatedAt || contact.updated_at
        }));

      const freshContacts = [...transformedContacts];
      setContacts(freshContacts);
      contactsRef.current = freshContacts;
      setContactsUpdateKey(prev => prev + 1);
    } catch (error) {
      console.error('Failed to fetch client contacts:', error);
      setContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  }, [client.id, token, isAuthenticated, loading]);

  useEffect(() => {
    if (activeTab === 'projects') {
      fetchClientProjects();
    }
    if (activeTab === 'contacts') {
      fetchClientContacts();
    }
  }, [activeTab, client.id, token, isAuthenticated, loading, fetchClientProjects, fetchClientContacts]);

  // Load initial data for overview
  useEffect(() => {
    fetchClientProjects();
    fetchClientContacts();
  }, [fetchClientProjects, fetchClientContacts]);

  const handleSaveEdit = async () => {
    try {
      setSaving(true);

      const backendData = {
        company_name: editData.name,
        contact_email: editData.email,
        contact_phone: editData.phone,
        address: editData.address,
        website: editData.website,
        industry: editData.industry,
        company_size: editData.companySize,
        annual_revenue: editData.annualRevenue,
        timezone: editData.timezone,
        status: editData.status === 'prospective' ? 'prospect' : editData.status
      };

      await api.put<any>(`/api/clients/${client.id}`, backendData, token!);

      Object.assign(client, editData);
      setIsEditing(false);
      alert('Client updated successfully!');
    } catch (error) {
      console.error('Failed to update client:', error);
      alert('Failed to update client. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      await api.delete<any>(`/api/clients/${client.id}`, token!);
      onBack();
    } catch (error) {
      console.error('Failed to delete client:', error);
      alert('Failed to delete client. Please try again.');
    } finally {
      setDeleteLoading(false);
      closeDeleteModal();
    }
  };

  const handleEditChange = (field: keyof Client, value: string) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddContact = async (contactData: NewClientContact) => {
    try {
      setAddingContact(true);

      const backendData = {
        first_name: contactData.firstName,
        last_name: contactData.lastName,
        email: contactData.email,
        phone: contactData.phone,
        position: contactData.position,
        department: contactData.department,
        is_primary: contactData.isPrimary,
        is_billing: contactData.isBilling,
        notes: contactData.notes
      };

      await api.post<any>(`/api/clients/${client.id}/contacts`, backendData, token!);
      await fetchClientContacts();
      closeAddContactModal();

    } catch (error) {
      console.error('Failed to add contact:', error);
      alert('Failed to add contact. Please try again.');
    } finally {
      setAddingContact(false);
    }
  };

  const handleDeleteContact = async (contactId: number) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }

    try {
      await api.delete(`/api/clients/${client.id}/contacts/${contactId}`, token!);
      await fetchClientContacts();
      alert('Contact deleted successfully!');
    } catch (error: any) {
      console.error('Failed to delete contact:', error);
      let errorMessage = 'Failed to delete contact. Please try again.';

      if (error.message && error.message.includes('Contact is already deleted')) {
        errorMessage = 'This contact has already been deleted.';
        setContacts(contacts.filter(contact => contact.id !== contactId));
      } else if (error.message && error.message.includes('Contact not found')) {
        errorMessage = 'Contact not found. It may have already been deleted.';
        await fetchClientContacts();
      } else if (error.message && error.message.includes('Access denied')) {
        errorMessage = 'You do not have permission to delete this contact.';
      }

      alert(errorMessage);
    }
  };

  const handleEditContact = async (contactData: NewClientContact) => {
    if (!editingContact) return;

    try {
      const backendData = {
        first_name: contactData.firstName,
        last_name: contactData.lastName,
        email: contactData.email,
        phone: contactData.phone,
        position: contactData.position,
        department: contactData.department,
        is_primary: contactData.isPrimary,
        is_billing: contactData.isBilling,
        notes: contactData.notes
      };

      await api.put(`/api/clients/${client.id}/contacts/${editingContact.id}`, backendData, token!);
      await fetchClientContacts();

      closeEditContactModal();
      setEditingContact(null);
      alert('Contact updated successfully!');
    } catch (error) {
      console.error('Failed to update contact:', error);
      alert('Failed to update contact. Please try again.');
    }
  };


  // Calculate stats
  const activeProjects = projects.filter(p => p.status === 'in_progress').length;
  const completedProjects = projects.filter(p => p.status === 'completed').length;
  const totalRevenue = projects.reduce((sum, p) => sum + (p.budget || 0), 0);
  const totalHours = projects.reduce((sum, p) => sum + (p.actualHours || 0), 0);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get key contacts (primary and billing)
  const keyContacts = contacts.filter(c => c.isPrimary || c.isBilling).slice(0, 3);
  const recentProjects = projects.slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-14 h-14 rounded-lg bg-gray-700 flex items-center justify-center">
                <span className="text-xl font-bold text-white">
                  {client.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-gray-900">{client.name}</h1>
                  {client.email && (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  )}
                </div>
                <button className="text-sm text-blue-600 hover:text-blue-700">Add Tags</button>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                size="md"
                icon={<Edit2 className="w-4 h-4" />}
                onClick={() => setIsEditing(true)}
              >
                Edit Client
              </Button>
              <Badge status={client.status} />
              <button
                onClick={openDeleteModal}
                className="text-gray-600 hover:text-gray-900"
              >
                <MoreVertical className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6">
          <div className="flex gap-8">
            {['Overview', 'Contacts', 'Projects', 'Notes'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab.toLowerCase() as any)}
                className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.toLowerCase()
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-6 py-6">
        {activeTab === 'overview' && (
          <>
            {/* Stats Row */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <Card>
                <div className="flex items-center justify-between mb-2">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <TrendingUp className="w-4 h-4 text-green-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">${totalRevenue.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">Total Revenue</p>
              </Card>

              <Card>
                <div className="flex items-center justify-between mb-2">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <FolderOpen className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{activeProjects}</p>
                <p className="text-xs text-gray-500 mt-1">Active Projects</p>
              </Card>

              <Card>
                <div className="flex items-center justify-between mb-2">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{completedProjects}</p>
                <p className="text-xs text-gray-500 mt-1">Completed Projects</p>
              </Card>

              <Card>
                <div className="flex items-center justify-between mb-2">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-orange-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{totalHours}h</p>
                <p className="text-xs text-gray-500 mt-1">Total Hours</p>
              </Card>
            </div>

            <div className="grid grid-cols-3 gap-6">
              {/* Left Column - Client Info */}
              <div className="col-span-2 space-y-6">
                {/* Contact Information */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-gray-900">Contact Information</h3>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Edit
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Mail className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Email</p>
                          <p className="text-sm text-gray-900 font-medium">{client.email}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Phone className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Phone</p>
                          <p className="text-sm text-gray-900 font-medium">{client.phone || 'Not provided'}</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Globe className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Website</p>
                          {client.website ? (
                            <a
                              href={client.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-700 font-medium break-all"
                            >
                              {client.website}
                            </a>
                          ) : (
                            <p className="text-sm text-gray-900 font-medium">Not provided</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-4 h-4 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Address</p>
                          <p className="text-sm text-gray-900 font-medium">{client.address || 'Not provided'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Company Details */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-gray-900">Company Details</h3>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Edit
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Industry</p>
                      <p className="text-sm text-gray-900">{client.industry || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Company Size</p>
                      <p className="text-sm text-gray-900">{client.companySize || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Annual Revenue</p>
                      <p className="text-sm text-gray-900">{client.annualRevenue || 'Not provided'}</p>
                    </div>
                  </div>
                </div>

                {/* Active Projects */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-gray-900">Recent Projects</h3>
                    <button
                      onClick={() => setActiveTab('projects')}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      View All
                    </button>
                  </div>
                  {loadingProjects ? (
                    <LoadingSpinner message="Loading projects..." />
                  ) : recentProjects.length > 0 ? (
                    <div className="space-y-3">
                      {recentProjects.map((project) => (
                        <div
                          key={project.id}
                          className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50/30 transition-all cursor-pointer"
                          onClick={() => onSelectProject && onSelectProject(project)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold text-gray-900">{project.name}</h4>
                            <Badge status={project.status} />
                          </div>
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                            <div className="flex items-center gap-4">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(project.dueDate)}
                              </span>
                              {project.budget && (
                                <span className="font-semibold text-gray-900">${project.budget.toLocaleString()}</span>
                              )}
                            </div>
                            <span>{(project as any).progress || 0}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-blue-600 h-1.5 rounded-full transition-all"
                              style={{ width: `${(project as any).progress || 0}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState
                      icon={FolderOpen}
                      title="No projects yet"
                      description=""
                    />
                  )}
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Key Contacts */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-gray-900">Key Contacts</h3>
                    <button
                      onClick={openAddContactModal}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  {loadingContacts ? (
                    <LoadingSpinner message="Loading contacts..." />
                  ) : keyContacts.length > 0 ? (
                    <div className="space-y-3">
                      {keyContacts.map((contact) => (
                        <div key={contact.id} className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-indigo-600">
                              {contact.firstName?.[0]}{contact.lastName?.[0]}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1">
                              <p className="text-sm font-medium text-gray-900">
                                {contact.firstName} {contact.lastName}
                              </p>
                              {contact.isPrimary && (
                                <Crown className="w-3 h-3 text-yellow-500" />
                              )}
                              {contact.isBilling && (
                                <CreditCard className="w-3 h-3 text-green-500" />
                              )}
                            </div>
                            <p className="text-xs text-gray-500">{contact.position || 'Contact'}</p>
                            <p className="text-xs text-gray-600 truncate">{contact.email}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500">No contacts yet</p>
                      <button
                        onClick={openAddContactModal}
                        className="text-sm text-blue-600 hover:text-blue-700 mt-2"
                      >
                        Add Contact
                      </button>
                    </div>
                  )}
                </div>

                {/* Recent Activity Placeholder */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-sm font-bold text-gray-900 mb-4">Recent Activity</h3>
                  <div className="text-center py-8 text-gray-500 text-sm">
                    Activity tracking coming soon
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'contacts' && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Contacts</h2>
              <Button
                variant="primary"
                size="md"
                icon={<Plus className="w-4 h-4" />}
                onClick={openAddContactModal}
              >
                Add Contact
              </Button>
            </div>
            {loadingContacts ? (
              <LoadingSpinner message="Loading contacts..." />
            ) : contacts.length > 0 ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Phone</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {contacts.map((contact) => (
                      <tr key={contact.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                              <span className="text-xs font-bold text-indigo-600">
                                {contact.firstName?.[0]}{contact.lastName?.[0]}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-sm font-medium text-gray-900">
                                {contact.firstName} {contact.lastName}
                              </span>
                              {contact.isPrimary && (
                                <Crown className="w-3 h-3 text-yellow-500" />
                              )}
                              {contact.isBilling && (
                                <CreditCard className="w-3 h-3 text-green-500" />
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{contact.position || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{contact.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{contact.phone || '-'}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => {
                              setEditingContact(contact);
                              openEditContactModal();
                            }}
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium mr-3"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteContact(contact.id)}
                            className="text-red-600 hover:text-red-700 text-sm font-medium"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState
                icon={User}
                title="No contacts yet"
                description="Add a contact to get started."
                action={
                  <Button
                    variant="primary"
                    size="md"
                    icon={<Plus className="w-4 h-4" />}
                    onClick={openAddContactModal}
                  >
                    Add Contact
                  </Button>
                }
              />
            )}
          </div>
        )}

        {activeTab === 'projects' && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Projects</h2>
            </div>
            {loadingProjects ? (
              <LoadingSpinner message="Loading projects..." />
            ) : projects.length > 0 ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Project Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Progress</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Value</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Due Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {projects.map((project) => (
                      <tr
                        key={project.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => onSelectProject && onSelectProject(project)}
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{project.name}</td>
                        <td className="px-4 py-3">
                          <Badge status={project.status} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-gray-200 rounded-full h-1.5">
                              <div
                                className="bg-blue-600 h-1.5 rounded-full"
                                style={{ width: `${(project as any).progress || 0}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-600">{(project as any).progress || 0}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                          {project.budget ? `$${project.budget.toLocaleString()}` : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(project.dueDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState
                icon={FolderOpen}
                title="No projects yet"
                description="Create a project for this client to get started."
              />
            )}
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Notes</h2>
              <Button
                variant="primary"
                size="md"
                icon={<Plus className="w-4 h-4" />}
              >
                Add Note
              </Button>
            </div>
            <div className="text-center py-12">
              <p className="text-gray-500">Notes management will be implemented next.</p>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {isEditing && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Client</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                  <input
                    type="text"
                    value={editData.name}
                    onChange={(e) => handleEditChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={editData.email}
                    onChange={(e) => handleEditChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={editData.phone || ''}
                    onChange={(e) => handleEditChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input
                    type="url"
                    value={editData.website || ''}
                    onChange={(e) => handleEditChange('website', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                  <input
                    type="text"
                    value={editData.industry || ''}
                    onChange={(e) => handleEditChange('industry', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Size</label>
                  <input
                    type="text"
                    value={editData.companySize || ''}
                    onChange={(e) => handleEditChange('companySize', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <textarea
                    value={editData.address || ''}
                    onChange={(e) => handleEditChange('address', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows={2}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => {
                    setIsEditing(false);
                    setEditData(client);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleSaveEdit}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Contact Modal */}
      {showAddContactModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <ContactForm
              onClose={closeAddContactModal}
              onSubmit={handleAddContact}
              loading={addingContact}
              clientId={client.id}
            />
          </div>
        </div>
      )}

      {/* Edit Contact Modal */}
      {isEditContactModalOpen && editingContact && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <ContactForm
              onClose={() => {
                closeEditContactModal();
                setEditingContact(null);
              }}
              onSubmit={handleEditContact}
              loading={false}
              clientId={client.id}
              initialData={editingContact}
              isEdit={true}
            />
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mt-4">Delete Client</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete <strong>{client.name}</strong>?
                  This action cannot be undone.
                </p>
              </div>
              <div className="flex items-center justify-center gap-2 px-4 py-3">
                <Button
                  variant="danger"
                  size="md"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? 'Deleting...' : 'Delete'}
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={closeDeleteModal}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDetail;